import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Trophy } from 'lucide-react';
import { db } from '@/lib/db';
import type { Course, HoleRange, Member, MemberPublic } from '@/lib/db/types';
import { EVENT_TEMPLATES, findEventTemplate } from '@/lib/events/event-templates';
import { GOLF_GAMES, findGameFormat } from '@/lib/reference/golf-games';
import { HANDICAP_ALLOWANCE } from '@/lib/games/handicap';
import { CAMPUS_ORDER, groupCoursesByCampus } from '@/lib/courses/group';
import { Sheet } from '@/components/shared/Sheet';
import { InvitePlayersPanel } from '@/components/shared/InvitePlayersPanel';
import { HolesSelector } from '@/components/shared/HolesSelector';
import { HandicapAllowancePicker } from '@/components/shared/HandicapAllowancePicker';
import { GameFormatGrid } from '@/components/games/GameFormatGrid';
import {
  TeamSetupPanel,
  buildTeamAssignments,
  teamFormatSummary,
  type TeamAssignment,
} from '@/components/games/TeamSetupPanel';

interface Props {
  member: Member;
  onClose: () => void;
  onCreated: (id: string) => void;
}

const TOURNAMENT_FORMATS = GOLF_GAMES.filter((g) => g.cat === 'Format' || g.cat === 'Wager' || g.cat === 'Team');
const FORM_ID = 'create-tournament-form';

export function CreateTournamentSheet({ member, onClose, onCreated }: Props) {
  const [step, setStep] = useState(1);
  const [courses, setCourses] = useState<Course[]>([]);
  const [clubMembers, setClubMembers] = useState<MemberPublic[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [courseId, setCourseId] = useState('');
  const [holes, setHoles] = useState<HoleRange>('full');
  const [eventTemplate, setEventTemplate] = useState<string>('tournament');
  const [startsAt, setStartsAt] = useState('');
  const [entryFee, setEntryFee] = useState('');
  const [handicapAllowance, setHandicapAllowance] = useState(HANDICAP_ALLOWANCE.stroke ?? 0.95);
  const [invitedIds, setInvitedIds] = useState<string[]>([]);
  const [guestDraft, setGuestDraft] = useState('');
  const [guestHandicapDraft, setGuestHandicapDraft] = useState('');
  const [guests, setGuests] = useState<Array<{ name: string; handicap?: number }>>([]);
  const [teamSize, setTeamSize] = useState(1);
  const [teamAssignment, setTeamAssignment] = useState<TeamAssignment>('auto');
  const [manualTeams, setManualTeams] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [format, setFormat] = useState('stroke');

  useEffect(() => {
    const tpl = findEventTemplate(eventTemplate);
    if (!tpl) return;
    setFormat(tpl.format);
    setHandicapAllowance(tpl.handicapAllowance);
    if (!name.trim()) setName(`${tpl.namePrefix} ${new Date().getFullYear()}`);
  }, [eventTemplate, name]);

  const formatDef = useMemo(() => findGameFormat(format), [format]);
  const teamCfg = formatDef?.teamConfig;

  useEffect(() => {
    void Promise.all([
      db.courses.list(),
      db.members.list({ limit: 100 }),
    ]).then(([c, m]) => {
      setCourses(c);
      setClubMembers(m.filter((x) => x.id !== member.id));
      if (c[0]) setCourseId(c[0].id);
    });
  }, [member.id]);

  useEffect(() => {
    setHandicapAllowance(HANDICAP_ALLOWANCE[format] ?? 1.0);
    if (teamCfg?.defaultTeamSize) setTeamSize(teamCfg.defaultTeamSize);
    else setTeamSize(1);
    setTeamAssignment('auto');
    setManualTeams({});
  }, [format, teamCfg?.defaultTeamSize]);

  const playerIds = useMemo(() => [member.id, ...invitedIds], [member.id, invitedIds]);
  const roster = useMemo(() => [
    { id: member.id, name: `${member.name} (you)` },
    ...clubMembers.filter((m) => invitedIds.includes(m.id)).map((m) => ({ id: m.id, name: m.name })),
  ], [member, clubMembers, invitedIds]);

  const toggleInvite = (id: string) => {
    setInvitedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const addGuest = () => {
    if (!guestDraft.trim()) return;
    const hcp = guestHandicapDraft.trim() ? parseFloat(guestHandicapDraft) : undefined;
    setGuests((g) => [...g, { name: guestDraft.trim(), handicap: hcp }]);
    setGuestDraft('');
    setGuestHandicapDraft('');
  };

  const teamsPreview = teamCfg?.isTeamFormat
    ? buildTeamAssignments({ teamSize, teamAssignment, playerIds, manualTeams })
    : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Enter a tournament name.');
      setStep(1);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const teamNote = teamCfg?.isTeamFormat ? `${teamFormatSummary(teamSize, teamAssignment)}\n` : '';
      const t = await db.tournaments.create({
        name: name.trim(),
        description: description.trim() || null,
        course_id: courseId || null,
        tee_id: null,
        format,
        scoring_basis: 'net',
        holes,
        starts_at: startsAt ? new Date(startsAt).toISOString() : null,
        ends_at: null,
        status: 'open',
        entry_fee_cents: entryFee ? Math.round(parseFloat(entryFee) * 100) : 0,
        cover_image_url: null,
        handicap_allowance: handicapAllowance,
        config: {
          guests,
          teams: teamsPreview,
          teamNote: teamNote.trim() || undefined,
          event_template: eventTemplate,
          ...(findEventTemplate(eventTemplate)?.config || {}),
        },
      });
      if (invitedIds.length) await db.tournaments.invite(t.id, invitedIds);
      onCreated(t.id);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const footer = step === 1 ? (
    <button
      type="button"
      onClick={() => {
        if (!name.trim()) {
          setError('Enter a tournament name to continue.');
          return;
        }
        setError(null);
        setStep(2);
      }}
      className="w-full rounded-full bg-navy py-3 text-sm font-bold text-cream"
    >
      Next: Players & Teams
    </button>
  ) : step === 2 ? (
    <button
      type="button"
      onClick={() => { setError(null); setStep(3); }}
      className="w-full rounded-full bg-navy py-3 text-sm font-bold text-cream"
    >
      Next: Stakes & Handicaps
    </button>
  ) : (
    <>
      <button
        type="button"
        onClick={() => { setError(null); setStep(2); }}
        className="w-full rounded-full border border-line py-3 text-sm font-semibold text-navy"
      >
        Back
      </button>
      <button
        type="button"
        disabled={submitting}
        onClick={() => void submit({ preventDefault: () => {} } as FormEvent)}
        className="w-full rounded-full bg-gold py-3 text-sm font-bold text-navy disabled:opacity-50"
      >
        {submitting ? 'Creating…' : 'Create Tournament'}
      </button>
    </>
  );

  return (
    <Sheet title="Create Tournament" onClose={onClose} footer={footer}>
      <form id={FORM_ID} onSubmit={submit} className="space-y-4 p-4">
        {step === 1 && (
          <>
            <div className="flex items-center gap-3 rounded-[14px] bg-navy p-4 text-paper shadow-bt-md">
              <Trophy size={32} className="shrink-0 text-gold" />
              <div>
                <div className="font-serif text-base font-bold tracking-tight">Run your own event</div>
                <div className="mt-0.5 text-[11px] text-white/70">Multi-round, multi-day, multi-player. Set the rules and the pot.</div>
              </div>
            </div>
            <div>
              <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-mute">Event type</div>
              <div className="grid grid-cols-2 gap-2">
                {EVENT_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => setEventTemplate(tpl.id)}
                    className={`rounded-xl border p-3 text-left ${eventTemplate === tpl.id ? 'border-navy bg-navy/5' : 'border-line bg-paper'}`}
                  >
                    <div className="text-xs font-bold text-ink">{tpl.label}</div>
                    <div className="mt-0.5 text-[10px] text-mute">{tpl.description}</div>
                  </button>
                ))}
              </div>
            </div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tournament name"
              className="w-full rounded-[10px] border border-line bg-paper px-3 py-2.5 text-sm font-semibold outline-none focus:border-gold"
              required
            />
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Quick pitch for prospective entrants"
              className="w-full resize-none rounded-[10px] border border-line bg-paper px-3 py-2.5 text-sm outline-none focus:border-gold"
            />
            <div>
              <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-mute">Course</div>
              <select
                value={courseId}
                onChange={(e) => setCourseId(e.target.value)}
                className="w-full rounded-[10px] border border-line bg-paper px-3 py-2.5 text-sm outline-none focus:border-gold"
              >
                {CAMPUS_ORDER.map((campus) => {
                  const grouped = groupCoursesByCampus(courses)[campus];
                  if (!grouped.length) return null;
                  return (
                    <optgroup key={campus} label={campus}>
                      {grouped.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </optgroup>
                  );
                })}
              </select>
            </div>
            <GameFormatGrid
              formats={TOURNAMENT_FORMATS}
              selectedId={format}
              onSelect={setFormat}
              label="Pick a format"
            />
            <HolesSelector value={holes} onChange={setHoles} />
            <div>
              <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-mute">Start date</div>
              <input
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className="w-full rounded-[10px] border border-line bg-paper px-3 py-2.5 text-sm outline-none focus:border-gold"
              />
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <InvitePlayersPanel
              members={clubMembers}
              selectedIds={invitedIds}
              onToggle={toggleInvite}
              guests={guests}
              guestDraft={guestDraft}
              guestHandicapDraft={guestHandicapDraft}
              onGuestDraftChange={setGuestDraft}
              onGuestHandicapDraftChange={setGuestHandicapDraft}
              onAddGuest={addGuest}
              onRemoveGuest={(i) => setGuests((g) => g.filter((_, idx) => idx !== i))}
              onUpdateGuestHandicap={(i, handicap) => {
                setGuests((g) => g.map((x, idx) => (idx === i ? { ...x, handicap } : x)));
              }}
              showGuests
              title="Invite players"
              searchPlaceholder="Search members to invite…"
            />
            {formatDef && (
              <TeamSetupPanel
                format={formatDef}
                teamSize={teamSize}
                teamAssignment={teamAssignment}
                onTeamSizeChange={setTeamSize}
                onTeamAssignmentChange={setTeamAssignment}
                totalParticipants={playerIds.length}
                players={roster}
                manualTeams={manualTeams}
                onManualTeamChange={(id, team) => setManualTeams((prev) => ({ ...prev, [id]: team }))}
              />
            )}
            {teamsPreview && teamsPreview.length > 0 && (
              <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
                {teamsPreview.map((t) => (
                  <div key={t.id}>Team {t.id}: {t.players.map((id) => roster.find((r) => r.id === id)?.name || 'Player').join(', ')}</div>
                ))}
              </div>
            )}
          </>
        )}

        {step === 3 && (
          <>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Entry fee ($)</label>
              <input type="number" value={entryFee} onChange={(e) => setEntryFee(e.target.value)} placeholder="Optional buy-in" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              <p className="mt-1 text-[10px] text-slate-500">Tracked for reference only — settled off-platform via Venmo.</p>
            </div>
            <HandicapAllowancePicker
              formatId={format}
              formatName={formatDef?.name}
              value={handicapAllowance}
              onChange={setHandicapAllowance}
            />
          </>
        )}

        {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      </form>
    </Sheet>
  );
}
